(ns hai.entity.helpers
  "Pathom EQL and Malli helpers for entity modelling"
  (:require
   [edn-query-language.core :as eql]
   [hai-model.model :as model]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]
   [malli.core :as m]
   [medley.core :refer [dissoc-in]]))

(defn query?
  [?query]
  (and (vector? ?query)
       (try
         (eql/query->ast ?query)
         (catch :default _))))

(def uuid-attr?
  (comp #{"uuid"} name))

(defn entity->uuid-attr
  [entity]
  (some (fn [[attr _]]
          (and (uuid-attr? attr)
               attr))
        entity))

(defn schema->uuid-attr
  [Entity]
  (->> Entity
       (m/children)
       (some (fn [[attr _ _]]
               (and (uuid-attr? attr)
                    attr)))))

(defn ->schema
  [?schema]
  (try
    (m/schema ?schema)
    (catch :default _)))

(let [uuid-attr->schema (into {}
                              (map (fn [schema]
                                     [(schema->uuid-attr schema) schema]))
                              model/Model)]
  (defn ->pull-query
    [?entity]
    (cond
      (->schema ?entity)
      (->> ?entity
           (m/children)
           (mapv
            (fn [[attr {:keys [optional]} _]]
              (if optional
                (list attr {:com.wsscode.pathom3.connect.operation/optional? true})
                attr))))

      (map? ?entity)
      (-> ?entity
          (entity->uuid-attr)
          (uuid-attr->schema)
          (->pull-query))

      :else
      (throw (ex-info "Unsupported input for `->pull-query`" {:?entity ?entity})))))

(let [uuid-attrs (into #{} (map schema->uuid-attr) model/Model)]
  (defn ->uuid-attr
    [?entity]
    (cond
      (->schema ?entity)
      (schema->uuid-attr ?entity)

      (map? ?entity)
      (entity->uuid-attr ?entity)

      :else
      (throw (ex-info "Unsupported input for `->uuid-attr`" {:?entity ?entity}))))

  (defn ->uuid
    [entity]
    (assert (map? entity))
    (let [uuid-attr (some (comp uuid-attrs first) entity)]
      (assert uuid-attr "UUID attr not found, check `hai.admin-ui.model`")
      (get entity uuid-attr)))

  (defn ->ident
    [entity]
    (assert (map? entity))
    (let [uuid-attr (some (comp uuid-attrs first) entity)]
      (assert uuid-attr "UUID attr not found, check `hai.admin-ui.model`")
      ;; TODO coercion!!!
      (let [uuid (-> (get entity uuid-attr) (str) (uuid))]
        [uuid-attr uuid]))))

(defn ->entity-query
  [entity]
  [{(->ident entity)
    (-> (->pull-query entity)
        (conj :user.entity/permissions
              ;; Always resolvable — the platform pads entries and returns []
              ;; for unshared entities.
              {:entity/shared-with [:entity/scope
                                    :user/uuid
                                    :user/email
                                    :user/full-name
                                    :entity/permissions]}))}])

(defn ->attr-schema
  [?schema]
  (when-let [schema (->schema ?schema)]
    (cond
      (= :map (m/type schema))
      (m/form schema)

      (#{:vector} (m/type schema))
      (some ->attr-schema (m/children schema)))))

(defn ->to-one-schema
  [?schema]
  (when-let [schema (->schema ?schema)]
    (if (#{:vector} (m/type schema))
      (some ->to-one-schema (m/children schema))
      (m/form schema))))

;; TODO malli registry with `:schema?` and `:attr-schema?`
(def attr-schema?
  [:and
   [:fn
    {:error/message "Must be a valid Malli schema"}
    ->schema]
   [:fn
    {:error/message "Must be a valid Malli `:map` (attr) schema"}
    ->attr-schema]])

(defevent-fx :entity/resolve
  ;; NOTE args after the first cofx arg become vector destructuring, this event can be called with 2
  ;; or 3 args
  ;;
  ;; `:pattern` lets a caller resolve the entity by its own shared eql (see
  ;; `hai.notes.eql/note-eql`, `hai.tasks.eql/task-eql`) instead of the shape
  ;; derived from the model. The model describes tags as `[:vector :string]`
  ;; while the resolvers return `{:group/uuid …}` maps, so the derived query
  ;; asks for a bare attr and a caller can never sub-select on one. Identity,
  ;; storage and unwrapping stay here either way.
  [_ entity args]
  (let [ident (->ident entity)]
    {:resolve (assoc (dissoc args :pattern)
                     :query (if-let [pattern (:pattern args)]
                              [{ident pattern}]
                              (->entity-query entity))
                     :transform-fn (fn [res]
                                     (get res ident))
                     :store-in ident)}))

(defsub :entity
  [db entity]
  (when (and (map? entity)
             (not-empty entity))
    (get-in db (->ident entity))))

(defevent :entity/clear
  [db entity]
  (dissoc-in db (->ident entity)))
